import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import styles from './conversation-feature-flags.module.scss';
import { get } from 'lodash-es';
import useSpotConfig from '@host-panel/request/config/use-config';
import { variablesList } from './types';
import Switch from '../../common/inputs/customs/switch';

type Props = {
  spotId: string;
  spotName: string;
};

const CssVariables: React.FC<Props> = ({ spotId, spotName }) => {
  const [customVariables, setCustomVariables] = useState<variablesList>({});
  const [isFinishedUploading, setIsFinishedUploading] = useState<boolean>(false);

  const [configValues, setConfig, { loading, saving, errorFetching, errorSaving }] = useSpotConfig('tenant.conversation', {}, true);

  const setField = useCallback((key: string) => (e: string) => {
    const customValues = {
      conversation: {
        ...customVariables['conversation'],
        [key]: {
          ...get(configValues, `conversation.${key}`),
          value: e
        }
      }
    };
    
    setCustomVariables(customValues);
  }, [configValues, customVariables])

  const getVariables = useCallback(() => {
    return Object.keys(configValues['conversation']).map(key => {
      const value = get(customVariables, `conversation.${key}.value`) ?? configValues['conversation'][key].value;

      let comp = typeof value === 'boolean' &&
        <Switch
          key={key}
          title={key}
          value={value}
          onChange={() => setField(key)(!value as any)}
        />
      
      return comp;
    })
  }, [configValues, customVariables])

  const saveSettings = useCallback(async () => {
      setIsFinishedUploading(false);
      const keys = customVariables['conversation'] && Object.keys(customVariables['conversation']);
    
    if (keys?.length > 0) {
      const newSettings = keys.map(key => {
        return { key: `tenant.conversation.${key}`, value: customVariables['conversation'][key].value }
      });
      
      setConfig(newSettings, spotName);
      setIsFinishedUploading(true);
    }
  }, [spotId, spotName, customVariables])

  const saveBtnClassName = classnames('button-green-medium', styles.saveBtn);
  const saveSpinnerClassName = classnames('spinner', 'loading-messages', styles.saveSpinner);

  if (errorFetching) {
    return (
      <div className={styles.spinnerContainer}>
        <span className={styles.errorMessage}>
          An error has occurred while fetching data. Please try again. 
        </span>
      </div>
    )
  }

  return (
    loading ?
      <div className={styles.spinnerContainer}>
        <span className="spinner loading-messages" />
      </div>
    :
      <>
        <h2 className={styles.title}>Default Values</h2>
        {getVariables()}
        <div className={styles.saveContainer}>
          <button
            className={saveBtnClassName}
            onClick={saveSettings}
          >
            Save Settings
          </button>
          {saving && 
            <span className={saveSpinnerClassName} />
          }
          {!saving && !errorSaving && isFinishedUploading &&
            <span className={styles.successMessage}>Saved Successfully!</span>
          }
          {errorSaving &&
            <span className={styles.errorMessage}>Failed Uploading!</span>
          }
        </div>
      </>
  )
};

export default CssVariables;
